import { Directive, ElementRef, HostListener, Input, OnChanges } from '@angular/core';
import { extractDigits } from './ssn.utils';

@Directive({
  selector: '[ssnMask]',
  standalone: true
})
export class SsnMaskDirective implements OnChanges {
  @Input('ssnMask') public editMode = false;
  @Input() public ssnMaskLockCaretToEnd = true;

  constructor(private readonly elementRef: ElementRef<HTMLInputElement>) {}

  public ngOnChanges(): void {
    this.applyMaskedValue();
  }

  @HostListener('input')
  public onInput(): void {
    this.applyMaskedValue();
  }

  private applyMaskedValue(): void {
    const input = this.elementRef.nativeElement;
    const masked = this.format(input.value ?? '');

    if (input.value !== masked) {
      input.value = masked;
    }

    if (this.editMode && this.ssnMaskLockCaretToEnd) {
      const caret = input.value.length;
      input.setSelectionRange(caret, caret);
    }
  }

  private format(value: string): string {
    const digits = extractDigits(value);

    if (this.editMode) {
      if (!this.ssnMaskLockCaretToEnd) {
        const raw = (value ?? '').toUpperCase();

        // Initial canonical value from form control (9 digits): show masked view.
        if (!raw.includes('X') && !raw.includes('-')) {
          const lastFour = digits.slice(-4);
          return lastFour ? `XXX-XX-${lastFour}` : '';
        }

        // Full-edit masked mode: preserve explicitly typed digits in any masked position.
        const padded = raw.padEnd(11, ' ');
        const slots = [0, 1, 2, 4, 5, 7, 8, 9, 10].map((position) => {
          const char = padded[position];
          return /\d/.test(char) ? char : 'X';
        });

        return `${slots[0]}${slots[1]}${slots[2]}-${slots[3]}${slots[4]}-${slots[5]}${slots[6]}${slots[7]}${slots[8]}`;
      }

      const lastFour = digits.slice(-4);
      return lastFour ? `XXX-XX-${lastFour}` : '';
    }

    const firstNine = digits.slice(0, 9);
    if (!firstNine) {
      return '';
    }

    const first = firstNine.slice(0, 3);
    const second = firstNine.slice(3, 5);
    const third = firstNine.slice(5, 9);

    if (firstNine.length <= 3) {
      return first;
    }

    if (firstNine.length <= 5) {
      return `${first}-${second}`;
    }

    return `${first}-${second}-${third}`;
  }
}
